/* eslint-disable no-loop-func */
import { toast } from 'react-toastify';
import { getAnalytics, logEvent } from 'firebase/analytics';
import store from './redux/store/AppStore';
import { SORT_MAP } from './redux/slices/leaderboardSlice';
import { OPEN_ENDED_MIN, OPEN_ENDED_MAX } from './filterDefaults';
import { User, UserManager, WebStorageStateStore } from 'oidc-client-ts';

export const getUser = () => {
    const oidcStorage = localStorage.getItem(
        `oidc.user:${window.cognitoAuthConfig.authority}:${window.cognitoAuthConfig.client_id}`
    );
    if (!oidcStorage) {
        return null;
    }

    const user = User.fromStorageString(oidcStorage);
    return user;
};

// Standalone UserManager used only for on-demand silent renew from inside
// authFetch. Shares storage (and therefore the current user) with the
// AuthProvider's internal UserManager via the same localStorage key.
let _userManager = null;
const getUserManager = () => {
    if (_userManager) return _userManager;
    if (!window.cognitoAuthConfig) return null;
    _userManager = new UserManager({
        ...window.cognitoAuthConfig,
        userStore: new WebStorageStateStore({ store: localStorage }),
    });
    return _userManager;
};

// fetch wrapper that attaches the current Bearer token. Proactively
// renews expired access tokens before sending, and falls back to an
// anonymous request when renewal fails — public endpoints (search,
// details, etc.) work anonymously, so dropping the dead token avoids a
// 401 → "Session expired" toast for visitors whose tab sat idle past
// the token's TTL. Callers own every other header (Content-type, etc.).
const trySilentRenew = async () => {
    const manager = getUserManager();
    if (!manager) return null;
    try {
        const refreshed = await manager.signinSilent();
        return refreshed?.access_token || null;
    } catch (e) {
        // Refresh token is dead or the IdP session was revoked. The
        // user is effectively signed out — surfacing the cause via
        // console keeps this debuggable without spamming a toast on
        // every request.
        // eslint-disable-next-line no-console
        console.warn('[authFetch] silent renew failed', e);
        return null;
    }
};

export const authFetch = async (url, init = {}) => {
    const buildInit = (token) => {
        const headers = { ...(init.headers || {}) };
        if (token) {
            headers.authorization = `Bearer ${token}`;
        }
        return { ...init, headers };
    };

    const user = getUser();
    let token = user?.access_token || null;

    // AuthProvider's automaticSilentRenew runs on a timer while the tab
    // is open, but a closed-and-reopened browser (or a long sleep) can
    // hand us an already-expired user before that timer gets a chance
    // to fire. Renew on demand so we never send a token we know is
    // stale.
    if (user?.expired) {
        token = await trySilentRenew();
    }

    let response = await fetch(url, buildInit(token));

    // Defensive: backend rejected what we thought was a fresh token
    // (clock skew, server-side revocation, race vs. the AuthProvider's
    // own renew, etc.). Try renew once more; if that still fails, retry
    // anonymously so public endpoints don't 401 on the caller.
    if (response.status === 401 && user) {
        const refreshedToken = await trySilentRenew();
        if (refreshedToken) {
            response = await fetch(url, buildInit(refreshedToken));
        } else if (token) {
            response = await fetch(url, buildInit(null));
        }
    }

    return response;
};

// SecretHash = base64(HMAC-SHA256(username + clientId, clientSecret)).
// Required by Cognito's user-pool API whenever the app client has a secret
// configured — which ours does (exposed in index.jsx, but that ship sailed).
const computeSecretHash = async (username) => {
    const { client_id: clientId, client_secret: clientSecret } = window.cognitoAuthConfig;
    const enc = new TextEncoder();
    const key = await crypto.subtle.importKey(
        'raw',
        enc.encode(clientSecret),
        { name: 'HMAC', hash: 'SHA-256' },
        false,
        ['sign']
    );
    const sig = await crypto.subtle.sign('HMAC', key, enc.encode(username + clientId));
    return btoa(String.fromCharCode(...new Uint8Array(sig)));
};

const callCognito = async (target, body) => {
    // authority is `https://cognito-idp.<region>.amazonaws.com/<userPoolId>`; the
    // user-pool API (ResendConfirmationCode, ConfirmSignUp, …) lives at the
    // region root, not the pool-qualified path — ClientId in the body
    // identifies the pool.
    const endpoint = new URL(window.cognitoAuthConfig.authority).origin + '/';
    const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/x-amz-json-1.1',
            'X-Amz-Target': `AWSCognitoIdentityProviderService.${target}`,
        },
        body: JSON.stringify(body),
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
        const err = new Error(data.message || `Cognito ${target} failed`);
        err.code = data.__type?.split('#').pop() || 'UnknownError';
        throw err;
    }
    return data;
};

export const resendConfirmationCode = async (email) => {
    const trimmed = email.trim().toLowerCase();
    const secretHash = await computeSecretHash(trimmed);
    return callCognito('ResendConfirmationCode', {
        ClientId: window.cognitoAuthConfig.client_id,
        Username: trimmed,
        SecretHash: secretHash,
    });
};

export const confirmSignUp = async (email, code) => {
    const trimmed = email.trim().toLowerCase();
    const secretHash = await computeSecretHash(trimmed);
    return callCognito('ConfirmSignUp', {
        ClientId: window.cognitoAuthConfig.client_id,
        Username: trimmed,
        ConfirmationCode: code.trim(),
        SecretHash: secretHash,
    });
};

export const getClientDomainPrefix = () => {
    const href = window.location.href;

    if (href.includes(`localhost`)) {
        return `http://localhost:3000`;
    } else if (href.includes(`192.168`)) {
        return `http://192.168.1.27:3000`;
    } else if (href.includes(`staging.commandersalt.com`)) {
        return `https://staging.commandersalt.com`;
    }

    return `https://commandersalt.com`;
};

export const getDomainPrefix = () => {
    const href = window.location.href;
    //
    // if (href.includes(`localhost`)) {
    //     return `http://localhost:3333`;
    // } else if (href.includes(`192.168`)) {
    //     return `http://192.168.1.27:3333`;
    // }
    //
    // if (href.includes('staging')) {
    //     return `https://api.commandersalt.com`;
    // }

    return `https://api.commandersalt.com`;
};

// Legacy slices sort by 'power' | 'salt' | 'bracket' | 'count'. /search uses
// type-specific axis names — translate so a deck slice and a user slice can
// pass the same SORT_MAP.POWERLEVEL.index without per-callsite knowledge.
const SEARCH_SORT_AXIS = {
    decks: {
        power: 'power',
        salt: 'salt',
        bracket: 'bracket',
        count: 'power',
        ingestdate: 'ingestdate',
        likecount: 'likecount',
    },
    commanders: {
        power: 'power',
        salt: 'salt',
        bracket: 'bracket',
        count: 'count',
        ingestdate: 'count',
        likecount: 'count',
    },
    users: {
        power: 'avgpower',
        salt: 'avgsalt',
        bracket: 'avgbracket',
        count: 'deckcount',
        ingestdate: 'deckcount',
        likecount: 'deckcount',
    },
    cards: {
        power: 'salt',
        salt: 'salt',
        bracket: 'salt',
        count: 'salt',
        ingestdate: 'salt',
        likecount: 'salt',
    },
};

// (legacy category, has-id-filter) → /search type + id-field rewrite.
// `category=commanders&id=X` historically returned *decks for that commander*;
// /search exposes that as `type=decks&commanderIndexId=X`. Same shape for
// authors (authorIndexId) and users (ownerId).
const resolveSearchTarget = (category, hasIdFilter) => {
    if (category === 'authors') {
        if (hasIdFilter) return { type: 'decks', idField: 'authorIndexId' };
        // Browse-by-author has no /search equivalent (see §8). Caller must
        // fall back to /list — return null to signal that.
        return null;
    }
    if (category === 'commanders' && hasIdFilter) return { type: 'decks', idField: 'commanderIndexId' };
    if (category === 'users' && hasIdFilter) return { type: 'decks', idField: 'ownerId' };
    if (['decks', 'commanders', 'users', 'cards'].includes(category)) {
        return { type: category, idField: null };
    }
    return null;
};

// /search hits use type-specific id fields (deckId/userId/cardId/...). Legacy
// reducers all key off `item.id`, so alias here once and downstream code stays
// unchanged across the cutover.
const aliasIdField = (hit, type) => {
    const sourceKey = { decks: 'deckId', cards: 'cardId', users: 'userId', commanders: 'commanderIndexId' }[type];
    if (!sourceKey || hit?.id !== undefined) return hit;
    return { ...hit, id: hit?.[sourceKey] };
};

export const getSearch = async (cursor, category, indexName, filters, direction, callback) => {
    const hasIdFilter = !!filters?.id;
    const target = resolveSearchTarget(category, hasIdFilter);
    if (!target) {
        // No /search equivalent for this surface — caller decides the fallback.
        return null;
    }

    const params = new URLSearchParams();
    params.set('type', target.type);

    const axisName = indexName || SORT_MAP.POWERLEVEL.index;
    const axis = SEARCH_SORT_AXIS[target.type]?.[axisName] || axisName;
    const directionPrefix = direction === SORT_MAP.DIRECTION.ASC ? '' : '-';
    params.set('sort', `${directionPrefix}${axis}`);

    if (typeof cursor === 'string' && cursor.length > 0) {
        params.set('cursor', cursor);
    }

    if (target.idField && hasIdFilter) {
        params.set(target.idField, filters.id);
    }

    if (filters?.colorFilters) {
        params.set('colorIdentity', filters.colorFilters);
    }

    // Per-axis range filters. The /search endpoint exposes the underlying
    // field for each axis differently per type (e.g. decks/commanders use
    // `saltRating`, users use `avgSaltRating`) — translate via this map so
    // the FilterPanel can stay type-agnostic.
    const RANGE_FIELDS_BY_TYPE = {
        decks: {
            power: 'powerLevelRating',
            bracket: 'bracketRating',
            salt: 'saltRating',
        },
        users: {
            power: 'avgPowerLevel',
            bracket: 'avgBracketRating',
            salt: 'avgSaltRating',
            deckCount: 'deckCount',
        },
        commanders: {
            power: 'powerLevelRating',
            bracket: 'bracketRating',
            salt: 'saltRating',
            deckCount: 'count',
        },
        cards: {
            salt: 'salt',
            price: 'price',
        },
    };
    const rangeFields = RANGE_FIELDS_BY_TYPE[target.type] || {};
    const newRanges = filters?.ranges || {};
    // Per-axis open-ended sentinels live in filterDefaults.js so the slider
    // bounds and the "skip _min/_max param" rule can never drift apart.
    Object.keys(newRanges).forEach((axis) => {
        const field = rangeFields[axis];
        if (!field) return;
        const r = newRanges[axis];
        if (r?.min !== undefined && r?.min !== null && r.min !== OPEN_ENDED_MIN[axis]) {
            params.set(`${field}_min`, r.min);
        }
        if (r?.max !== undefined && r?.max !== null && r.max !== OPEN_ENDED_MAX[axis]) {
            params.set(`${field}_max`, r.max);
        }
    });

    if (filters?.query) {
        params.set('query', filters.query);
    }

    if (filters?.archetypeMajor && filters.archetypeMajor !== 'any') {
        params.set('archetypeMajor', filters.archetypeMajor);
    }
    if (filters?.archetypeMinor && filters.archetypeMinor !== 'any') {
        params.set('archetypeMinor', filters.archetypeMinor);
    }
    // Legacy combined `archetypes` string ("major,minor") still in use by
    // the Archetypes browse page until that rewires to the new shape.
    if (filters?.archetypes) {
        const [major, minor] = String(filters.archetypes).split(',');
        if (major) params.set('archetypeMajor', major);
        if (minor) params.set('archetypeMinor', minor);
    }

    const fetchUri = `${getDomainPrefix()}/search?${params.toString()}`;

    // Only spin up authFetch when an authenticated user actually exists —
    // skips the localStorage / UserManager dance for anonymous visitors.
    // authFetch itself proactively renews expired tokens and falls back
    // to anonymous on renew failure, so /search behaves correctly even
    // when a previous session has gone stale.
    const fetcher = getUser() ? authFetch : fetch;

    try {
        const response = await fetcher(fetchUri, {
            method: 'GET',
            headers: { 'Content-type': 'application/json;charset=UTF-8' },
        });
        const body = await response.json().catch(() => ({}));

        if (!response.ok) {
            // Surface a user-facing message — without it a stale Cognito
            // session or backend hiccup just renders a blank list with no
            // explanation. Per the migration doc §4 we don't echo the
            // server's `body.error` verbatim; pick a category-appropriate
            // line and let the caller see the raw status code if it cares.
            const status = response.status;
            let message;
            if (status === 401) {
                message = `Session expired — sign in again to keep browsing.`;
            } else if (status === 503) {
                message = `Search is temporarily unavailable. Try again in a moment.`;
            } else if (status >= 500) {
                message = `Search hit a snag. Try again in a moment.`;
            } else if (status === 400 && body?.error === 'invalid_cursor') {
                // Tell the caller specifically — slices may want to reset
                // pagination instead of toasting at the user.
                message = null;
            } else {
                message = `Couldn't run that search. Try simplifying your filters.`;
            }
            if (message) toast(message, { type: status === 401 ? 'warning' : 'error' });
            callback({ Items: [], LastEvaluatedKey: null, Count: 0, error: body?.error, status });
            return;
        }

        const hits = Array.isArray(body?.hits) ? body.hits.map((hit) => aliasIdField(hit, target.type)) : [];
        // Adapt to legacy { Items, LastEvaluatedKey } so the 8 slice callers
        // need zero changes for the cutover. cursor is null on last page.
        callback({
            Items: hits,
            LastEvaluatedKey: body?.cursor || null,
            Count: hits.length,
            total: body?.total,
        });
    } catch (err) {
        toast(`Couldn't reach the search service. Check your connection.`, { type: 'error' });
        callback({ Items: [], LastEvaluatedKey: null, Count: 0, error: err?.message || 'network error', status: 0 });
    }
};

/**
 * Encode a params object as a URLSearchParams-compatible query string for
 * the /upgrades family. See `getDeckUpgrades` for the encoding contract.
 */
const encodeUpgradesParams = (params) => {
    const qs = [];
    Object.keys(params || {}).forEach((key) => {
        const value = params[key];
        if (value == null) return;
        if (Array.isArray(value)) {
            if (value.length === 0) return;
            qs.push(`${encodeURIComponent(key)}=${encodeURIComponent(JSON.stringify(value))}`);
            return;
        }
        if (typeof value === 'object') {
            qs.push(`${encodeURIComponent(key)}=${encodeURIComponent(JSON.stringify(value))}`);
            return;
        }
        if (typeof value === 'string' && value.length === 0) return;
        if (typeof value === 'boolean') {
            qs.push(`${encodeURIComponent(key)}=${value ? 'true' : 'false'}`);
            return;
        }
        qs.push(`${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`);
    });
    return qs.join('&');
};

/**
 * Shared fetch/error handling for the /upgrades family. Surfaces:
 *   - `error` (canonical code from the body when available, else "HTTP N")
 *   - `status` (numeric HTTP status; 0 on network failure)
 *   - `retryAfter` (parsed seconds from the Retry-After response header —
 *      surfaced on 503 kill-switch responses so the UI can suppress
 *      retries until the timer elapses)
 *   - `message` (free-form body message if present)
 *
 * On 2xx the raw body is passed through to the callback as-is.
 */
const upgradesRequest = async (path, params, callback) => {
    const qs = encodeUpgradesParams(params);
    const fetchUri = `${getDomainPrefix()}${path}${qs ? `?${qs}` : ''}`;
    try {
        const response = await fetch(fetchUri, {
            method: 'GET',
            headers: { 'Content-type': 'application/json;charset=UTF-8' },
        });
        const body = await response.json().catch(() => ({}));
        if (!response.ok) {
            const retryHeader = response.headers?.get?.('Retry-After');
            const retryAfter = retryHeader ? parseInt(retryHeader, 10) : null;
            callback({
                error: body?.error || `HTTP ${response.status}`,
                message: body?.message || body?.reason || null,
                status: response.status,
                retryAfter: Number.isFinite(retryAfter) ? retryAfter : null,
            });
            return;
        }
        callback(body);
    } catch (err) {
        callback({ error: err?.message || 'network error', status: 0 });
    }
};

export const DynamoConnector = {
    getUserDetails: async (id, callback) => {
        try {
            let fetchUri = `${getDomainPrefix()}/details?id=${id}&isUser=true`;

            const results = await (
                await authFetch(fetchUri, {
                    method: 'GET',
                    headers: { 'Content-type': 'application/json;charset=UTF-8' },
                })
            ).json();

            callback(results);
        } catch (error) {
            toast(`Unable to find user`);
            callback(null);
        }
    },
    // Fetch the single-call user-stats envelope described in the
    // user-stats integration guide. Returns:
    //   { status, eligible, deckCount, lastUpdatedAt, rebuildGeneration,
    //     playerType, projection }
    // `id` accepts either the UUID ownerId or the username — the API
    // resolves both. Returns null on any error; the caller renders the
    // "still computing" placeholder.
    getUserCardStats: async (idOrUsername, callback) => {
        if (!idOrUsername) {
            callback && callback(null);
            return;
        }
        try {
            const fetchUri = `${getDomainPrefix()}/usercardstats?id=${encodeURIComponent(idOrUsername)}`;
            // Public endpoint — issue as a "simple" CORS request: no
            // Authorization header (preflight trigger) and no custom
            // Content-type (application/json also triggers preflight).
            // The response is JSON regardless; we parse it below.
            const response = await fetch(fetchUri, { method: 'GET' });
            if (!response.ok) {
                callback && callback(null);
                return;
            }
            const data = await response.json();
            callback && callback(data && typeof data === 'object' ? data : null);
        } catch (error) {
            callback && callback(null);
        }
    },
    deletePatreonLink: async (callback) => {
        try {
            let fetchUri = `${getDomainPrefix()}/patreon`;

            const results = await authFetch(fetchUri, {
                method: 'DELETE',
                headers: { 'Content-type': 'application/json;charset=UTF-8' },
            });

            if (results.status > 200) {
                callback(false);
            } else {
                toast(`Patreon link deleted`);
                callback(true);
            }
        } catch (error) {
            callback(false);
        }
    },
    postUpdateUserPreferences: async (payload = [], callback) => {
        try {
            let fetchUri = `${getDomainPrefix()}/user?preferences=${encodeURIComponent(JSON.stringify(payload))}`;

            await authFetch(fetchUri, {
                method: 'POST',
                headers: { 'Content-type': 'application/json;charset=UTF-8' },
            });

            toast(`Preferences saved`, { type: 'success' });
            callback(null);
        } catch (error) {
            toast(`Failed to save preferences`);
            callback(null);
        }
    },
    uploadAvatar: async (file, size, callback) => {
        try {
            let fetchUri = `${getDomainPrefix()}/uploadavatar`;
            const formData = new FormData();
            formData.append(
                'data',
                JSON.stringify({
                    name: file.name,
                    description: file.description,
                })
            );
            formData.append(`files`, file, file.name);

            const results = await authFetch(fetchUri, {
                method: 'POST',
                body: formData,
            });

            toast(`Avatar updated`, { type: 'success' });
            callback(results);
        } catch (error) {
            toast(`Failed to update avatar`);
            callback(null);
        }
    },
    getRefreshPatreonMemberships: async (callback) => {
        try {
            let fetchUri = `${getDomainPrefix()}/patreon`;

            const results = await authFetch(fetchUri, {
                method: 'GET',
                headers: { 'Content-type': 'application/json;charset=UTF-8' },
            });

            if (results.status > 200) {
                callback(false);
            } else {
                toast(`Patreon data refreshed`);
                callback(true);
            }
        } catch (error) {
            callback(false);
        }
    },
    validatePatreonCode: async (code, callback) => {
        try {
            let fetchUri = `${getDomainPrefix()}/patreon?code=${code}`;

            const results = await authFetch(fetchUri, {
                method: 'POST',
                headers: { 'Content-type': 'application/json;charset=UTF-8' },
            });

            callback(results);
        } catch (error) {
            callback(null);
        }
    },
    deleteAccountLink: async (list, callback) => {
        try {
            let fetchUri = `${getDomainPrefix()}/linkaccount?list=${encodeURIComponent(JSON.stringify(list))}`;

            const results = await authFetch(fetchUri, {
                method: 'DELETE',
                headers: { 'Content-type': 'application/json;charset=UTF-8' },
            });

            if (results.status > 200) {
                callback(false);
            } else {
                toast(`Linked account(s) deleted`);
                callback(true);
            }
        } catch (error) {
            callback(false);
        }
    },
    postValidateLinkCode: async (provider, username, callback) => {
        try {
            let fetchUri = `${getDomainPrefix()}/linkaccount?provider=${provider}&username=${username}`;

            const results = await authFetch(fetchUri, {
                method: 'POST',
                headers: { 'Content-type': 'application/json;charset=UTF-8' },
            });

            if (results.status > 200) {
                logEvent(getAnalytics(), 'profile_link_failed', { provider, username, status: results.status });
                callback(false);
            } else {
                logEvent(getAnalytics(), 'profile_link_success', { provider, username });
                callback(true);
            }
        } catch (error) {
            logEvent(getAnalytics(), 'profile_link_failed', { provider, username, error: String(error) });
            callback(false);
        }
    },
    postManagedDecksOp: async (operation, deckIds = [], callback) => {
        try {
            let fetchUri = `${getDomainPrefix()}/managedecks?operation=${operation}&list=${encodeURIComponent(
                JSON.stringify(deckIds)
            )}`;

            const results = await (
                await authFetch(fetchUri, {
                    method: 'POST',
                    headers: { 'Content-type': 'application/json;charset=UTF-8' },
                })
            ).json();

            callback(results);
        } catch (error) {
            callback(null);
        }
    },
    getManagedDecks: async (callback) => {
        try {
            let fetchUri = `${getDomainPrefix()}/managedecks`;

            const results = await (
                await authFetch(fetchUri, {
                    method: 'GET',
                    headers: { 'Content-type': 'application/json;charset=UTF-8' },
                })
            ).json();

            callback(results);
        } catch (error) {
            callback(null);
        }
    },
    getAccountLinkCode: async (provider, callback) => {
        try {
            let fetchUri = `${getDomainPrefix()}/linkaccount?provider=${provider}`;

            const results = await (
                await authFetch(fetchUri, {
                    method: 'GET',
                    headers: { 'Content-type': 'application/json;charset=UTF-8' },
                })
            ).text();

            callback(results);
        } catch (error) {
            callback(null);
        }
    },
    getUserProfile: async (callback) => {
        try {
            let fetchUri = `${getDomainPrefix()}/user`;

            const results = await (
                await authFetch(fetchUri, {
                    method: 'GET',
                    headers: { 'Content-type': 'application/json;charset=UTF-8' },
                })
            ).json();
            callback(results);
        } catch (error) {
            callback(null);
        }
    },
    getBatchRefreshAuthorDeckList: async (list, callback) => {
        const promises = [];
        list.forEach((item) => {
            let fetchUri = `${getDomainPrefix()}/details?id=${item.id}`;
            promises.push(
                fetch(fetchUri, {
                    method: 'GET',
                    headers: {
                        'Content-type': 'application/json;charset=UTF-8',
                    },
                })
            );
        });

        await Promise.all(promises);
        callback && callback();
    },
    getStats: async (callback) => {
        let fetchUri = `${getDomainPrefix()}/stats`;

        const results = await (
            await fetch(fetchUri, {
                method: 'GET',
                headers: {
                    'Content-type': 'application/json;charset=UTF-8',
                },
            })
        ).json();

        callback(results);
    },
    getDeck: async (id, callback, errorCallback) => {
        try {
            const startTime = performance.now();

            const deck = store.getState().preview.cache?.[id];
            if (deck) {
                callback(deck);

                // bail early
                return;
            }

            let fetchUri = `${getDomainPrefix()}/decks?id=${id}`;

            let results = await (
                await authFetch(fetchUri, {
                    method: 'GET',
                    headers: { 'Content-type': 'application/json;charset=UTF-8' },
                })
            ).json();

            const endTime = performance.now();
            const responseTime = endTime - startTime;

            try {
                let _newJson = null;
                let strJson = JSON.stringify(results);

                strJson = strJson.replace(/\._/g, `__`);

                _newJson = JSON.parse(strJson);

                results = _newJson;
            } catch (error) {
                console.log(`crap`, error);
            }

            callback && callback(results);
        } catch (error) {
            toast(`Unable to find deck`);
            callback(null);
        }
    },
    getCards: async (deckId, callback, errorCallback) => {
        try {
            const fetchUri = `${getDomainPrefix()}/cards?deckId=${deckId}`;
            const response = await fetch(fetchUri, {
                method: 'GET',
                headers: { 'Content-type': 'application/json;charset=UTF-8' },
            });
            if (!response.ok) {
                const err = await response.json().catch(() => ({}));
                throw new Error(err.error || `HTTP ${response.status}`);
            }
            const results = await response.json();
            callback && callback(results);
        } catch (error) {
            if (errorCallback) {
                errorCallback(error);
            } else {
                toast(`Unable to load card data`);
            }
        }
    },
    /**
     * Fetch live pricing for a card from Scryfall. Our own `price` field
     * is refreshed infrequently; Scryfall updates daily and is authoritative
     * for USD/EUR/foil/etched variants. Returns the `prices` object
     * ({ usd, usd_foil, usd_etched, eur, eur_foil, tix }) or null on error.
     *
     * Uses the `?exact=` name lookup since it's single-request, cacheable,
     * and doesn't require Scryfall's internal card UUID.
     *
     * See https://scryfall.com/docs/api/cards/named for endpoint details.
     */
    getScryfallPrice: async (cardName, callback) => {
        if (!cardName) {
            callback && callback(null);
            return;
        }
        try {
            const encoded = encodeURIComponent(cardName);
            const response = await fetch(`https://api.scryfall.com/cards/named?exact=${encoded}`, {
                method: 'GET',
                headers: { Accept: 'application/json' },
            });
            if (!response.ok) {
                callback && callback(null);
                return;
            }
            const data = await response.json();
            callback && callback(data?.prices || null);
        } catch (error) {
            callback && callback(null);
        }
    },
    /**
     * Fetch every printing of a given card name from Scryfall so the card
     * dialog can list prices per print (set, collector number, release date,
     * finish, rarity, USD / USD-foil / USD-etched).
     *
     * Uses `cards/search?q=!"<name>"&unique=prints&order=released` — the
     * bang-exact syntax keeps false positives out (e.g. no matching
     * `Rhystic Study // Token` variants).
     */
    /**
     * Bulk pricing + vendor URL lookup for a list of card names. Used by
     * grids (Upgrade Finder, etc.) where we want a fresh `prices.usd`
     * and a direct `purchase_uris.tcgplayer` for each card without firing
     * a Scryfall request per tile.
     *
     * Scryfall caps `/cards/collection` at 75 identifiers per call; we
     * chunk and run batches sequentially (their rate-limit guidance asks
     * for ~50–100ms between requests, which sequential awaits give for
     * free). Returns a map keyed by lowercased card name so callers can
     * lookup without worrying about Scryfall's canonical casing.
     *
     * See https://scryfall.com/docs/api/cards/collection for the API.
     */
    getScryfallCollection: async (cardNames, callback) => {
        const unique = Array.from(
            new Set((Array.isArray(cardNames) ? cardNames : []).filter((n) => typeof n === 'string' && n.length > 0))
        );
        if (unique.length === 0) {
            callback && callback({});
            return;
        }
        const out = {};
        try {
            for (let i = 0; i < unique.length; i += 75) {
                const chunk = unique.slice(i, i + 75);
                const response = await fetch('https://api.scryfall.com/cards/collection', {
                    method: 'POST',
                    headers: {
                        Accept: 'application/json',
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        identifiers: chunk.map((name) => ({ name })),
                    }),
                });
                if (!response.ok) continue;
                const data = await response.json();
                const cards = Array.isArray(data?.data) ? data.data : [];
                for (const c of cards) {
                    const key = (c?.name || '').toLowerCase();
                    if (!key) continue;
                    out[key] = {
                        name: c.name,
                        usd: c.prices?.usd != null ? Number(c.prices.usd) : null,
                        usdFoil: c.prices?.usd_foil != null ? Number(c.prices.usd_foil) : null,
                        purchaseUris: c.purchase_uris || {},
                    };
                }
            }
            callback && callback(out);
        } catch (error) {
            callback && callback(out);
        }
    },
    getScryfallPrints: async (cardName, callback) => {
        if (!cardName) {
            callback && callback(null);
            return;
        }
        try {
            // `game:paper` excludes MTGO-only printings (Masters Edition,
            // Vintage Masters, Tempest Remastered, etc.) whose "USD"
            // prices are MTGO ticket values rather than real money and
            // are never actually buyable on TCGPlayer. Bang-exact
            // match (`!"<name>"`) keeps fuzzy variants out.
            const q = encodeURIComponent(`!"${cardName}" game:paper`);
            const uri = `https://api.scryfall.com/cards/search?q=${q}&unique=prints&order=released`;
            const response = await fetch(uri, {
                method: 'GET',
                headers: { Accept: 'application/json' },
            });
            if (!response.ok) {
                callback && callback(null);
                return;
            }
            const data = await response.json();
            // Defensive: even with game:paper in the query, drop any
            // print Scryfall flags as digital-only so we never surface
            // MTGO ticket prices as if they were USD.
            const paperPrints = Array.isArray(data?.data) ? data.data.filter((c) => !c?.digital) : [];
            const prints = paperPrints.length
                ? paperPrints.map((c) => ({
                      id: c.id,
                      name: c.name,
                      set: c.set,
                      setName: c.set_name,
                      collectorNumber: c.collector_number,
                      releasedAt: c.released_at,
                      prices: c.prices || {},
                      rarity: c.rarity,
                      finishes: c.finishes || [],
                      promo: c.promo,
                      frameEffects: c.frame_effects || [],
                      scryfallUri: c.scryfall_uri,
                      imageUri: c.image_uris?.small || c.image_uris?.normal || null,
                      // Raw bag of vendor purchase URLs from Scryfall —
                      // affiliate providers consume this directly (see
                      // src/affiliates/providers/*). Includes tcgplayer,
                      // cardmarket, cardhoarder when available.
                      purchaseUris: c.purchase_uris || {},
                  }))
                : [];
            callback && callback(prints);
        } catch (error) {
            callback && callback(null);
        }
    },
    getCardDetails: async (id, callback) => {
        let fetchUri = `${getDomainPrefix()}/details?id=${id?.replace(/-/g, '_')}&isCard=true`;

        const results = await (
            await fetch(fetchUri, {
                method: 'GET',
                headers: {
                    'Content-type': 'application/json;charset=UTF-8',
                },
            })
        ).json();

        callback(results);
    },
    getCommanderDetails: async (id, callback) => {
        const canonicalId = id?.replace(/-/g, '_');
        const fetchUri = `${getDomainPrefix()}/details?id=${canonicalId}&isCommander=true`;

        const response = await fetch(fetchUri, {
            method: 'GET',
            headers: {
                'Content-type': 'application/json;charset=UTF-8',
            },
        });

        // 400 → malformed id, 404 → unknown commander. Surface both as an
        // explicit error shape so the caller can toast + redirect instead of
        // rendering a zero-stub record (the pre-cleanup API used to return
        // that for any id, which hid real 404s).
        if (response.status === 400 || response.status === 404) {
            const body = await response.json().catch(() => ({}));
            callback({ error: body?.error || 'commander not found', status: response.status, id: canonicalId });
            return;
        }

        const results = await response.json();
        callback(results);
    },
    getCommanderStatsHealth: async (id, callback) => {
        try {
            const normalizedId = id?.replace(/-/g, '_');
            const fetchUri = `${getDomainPrefix()}/commandercardstats?id=${normalizedId}&mode=health`;

            const response = await fetch(fetchUri, {
                method: 'GET',
                headers: {
                    'Content-type': 'application/json;charset=UTF-8',
                },
            });

            if (response.status === 503) {
                callback({ disabled: true, data: null });
                return;
            }

            if (!response.ok) {
                callback({ disabled: false, data: null, badRequest: response.status === 400 });
                return;
            }

            const data = await response.json();
            callback({ disabled: false, data });
        } catch (error) {
            callback({ disabled: false, data: null });
        }
    },
    getCommanderCardStats: async (id, binKey, callback, archetypeFilter = null) => {
        try {
            const normalizedId = id?.replace(/-/g, '_');
            const params = [`id=${normalizedId}`];
            // bracket/power is always required by the API — archetype is
            // strictly additive, never a standalone selector.
            if (binKey?.startsWith('bracket_')) {
                params.push(`bracket=${binKey.substring('bracket_'.length)}`);
            } else if (binKey?.startsWith('power_')) {
                params.push(`power=${binKey.substring('power_'.length)}`);
            } else {
                callback({ disabled: false, data: null, badRequest: true });
                return;
            }
            const major = archetypeFilter?.archetype;
            const sub = archetypeFilter?.subArchetype;
            if (major) params.push(`archetype=${encodeURIComponent(major)}`);
            if (sub) params.push(`subArchetype=${encodeURIComponent(sub)}`);

            const fetchUri = `${getDomainPrefix()}/commandercardstats?${params.join('&')}`;

            const response = await fetch(fetchUri, {
                method: 'GET',
                headers: {
                    'Content-type': 'application/json;charset=UTF-8',
                },
            });

            if (response.status === 503) {
                callback({ disabled: true, data: null });
                return;
            }

            if (!response.ok) {
                callback({ disabled: false, data: null, badRequest: response.status === 400 });
                return;
            }

            const data = await response.json();
            callback({ disabled: false, data });
        } catch (error) {
            callback({ disabled: false, data: null });
        }
    },
    getAuthorDetails: async (id, callback) => {
        const fetchUri = `${getDomainPrefix()}/details?id=${id}&isAuthor=true`;

        const response = await fetch(fetchUri, {
            method: 'GET',
            headers: {
                'Content-type': 'application/json;charset=UTF-8',
            },
        });

        if (response.status === 400 || response.status === 404) {
            const body = await response.json().catch(() => ({}));
            callback({ error: body?.error || 'author not found', status: response.status, id });
            return;
        }

        const results = await response.json();
        callback(results);
    },
    getCEDHMeta: async (callback) => {
        let fetchUri = `${getDomainPrefix()}/meta`;

        const results = await (
            await fetch(fetchUri, {
                method: 'GET',
                headers: {
                    'Content-type': 'application/json;charset=UTF-8',
                },
            })
        ).json();

        callback(results);
    },
    /**
     * Transport for `/upgrades` (card_category path) and the source-based
     * variants. After the base-search overhaul the endpoint is unified —
     * `source=card_category` is the default and accepts the full new filter
     * surface (qualifiers, q, pips, cmcMin/Max, priceMax, effectTypes,
     * actionScope, trigger/result groups, combosWithInDeck, matchesPerCard,
     * limit, multi-value category, …). The other source paths
     * (bracket/power/combos/…) are unchanged.
     *
     * Param encoding:
     *   - arrays → JSON-stringified (matches API Gateway's expected shape
     *     for the multi-value contract; the endpoint also accepts repeated
     *     keys + comma-joined values)
     *   - objects (qualifiers, pips, legacy Dynamo cursor) → JSON-stringified
     *   - scalars → URL-encoded as strings
     *   - null / undefined / empty-string / empty-array values are dropped
     */
    getDeckUpgrades: async (params = {}, callback) => upgradesRequest('/upgrades', params, callback),
    getDeckUpgradesExtended: async (params = {}, callback) => upgradesRequest('/upgrades', params, callback),
    /**
     * Per-(cardId, category) full-match-list endpoint. Drives the
     * "See all matches" affordance on a card whose main /upgrades response
     * had matchesTruncated=true. Accepts id, category, optional limit
     * (default 50, max 200), optional cursor.
     */
    getUpgradeMatchDetail: async (params = {}, callback) => upgradesRequest('/upgrades/match-detail', params, callback),
    getList: async (cursor, category, index = null, filters, direction = SORT_MAP.DIRECTION.DESC, callback) => {
        // Browse-by-author (category=authors with no id) has no /search
        // equivalent (see §8) — resolveSearchTarget returns null and we fall
        // through to the legacy /list path. Everything else routes to /search.
        const hasIdFilter = !!filters?.id;
        if (resolveSearchTarget(category, hasIdFilter)) {
            return getSearch(cursor, category, index, filters, direction, callback);
        }

        let fetchUri = `${getDomainPrefix()}/list?category=${category}`;

        if (index === null) {
            index = SORT_MAP.POWERLEVEL.index;
        }

        // console.log(`=> ${JSON.stringify(direction)}`);
        const directionPrefix = direction === SORT_MAP.DIRECTION.DESC ? '' : '-';

        fetchUri = `${fetchUri}&sort=${directionPrefix}${index}`;

        if (cursor) {
            cursor = encodeURIComponent(JSON.stringify(cursor));
            fetchUri = `${fetchUri}&cursor=${cursor}`;
        }

        if (filters) {
            if (filters?.colorFilters) {
                fetchUri = `${fetchUri}&colorIdentity=${filters.colorFilters}`;
            }

            if (filters?.range) {
                fetchUri = `${fetchUri}&high=${filters.range.high}&low=${filters.range.low}`;
            }

            if (filters?.query) {
                fetchUri = `${fetchUri}&query=${filters.query}`;
            }

            if (filters?.id) {
                fetchUri = `${fetchUri}&id=${filters.id}`;
            }

            if (filters.archetypes) {
                fetchUri = `${fetchUri}&archetypes=${filters.archetypes}`;
            }
        }

        const results = await (
            await fetch(fetchUri, {
                method: 'GET',
                headers: {
                    'Content-type': 'application/json;charset=UTF-8',
                },
            })
        ).json();

        let sortKey = null;
        switch (index) {
            case 'salt':
                sortKey = SORT_MAP.SALT.field;
                break;
            case 'bracket':
                sortKey = SORT_MAP.BRACKET.field;
                break;
            case 'count':
                sortKey = SORT_MAP.COUNT.field;
                break;
            default:
                sortKey = SORT_MAP.POWERLEVEL.field;
        }

        if (sortKey) {
            results.Items = results.Items.sort((a, b) => {
                if (direction === 'desc') {
                    if (a[sortKey] < b[sortKey]) {
                        return 1;
                    }

                    return -1;
                }

                if (a[sortKey] > b[sortKey]) {
                    return 1;
                }

                return -1;
            });
        }

        callback(results);
    },
    addAuthorToPod: async (authorId, podId, doneCallback, errorCallback) => {
        const analytics = getAnalytics();

        try {
            const request = await fetch(`${getDomainPrefix()}/pods/authors/add?podId=${podId}&authorId=${authorId}`, {
                method: 'GET',
                headers: {
                    'Content-type': 'application/json;charset=UTF-8',
                },
            });
            const response = await request.json();

            if (request.status !== 200) {
                throw response;
            }

            doneCallback({
                ...response?.pod,
                key: response?.pod?.id,
            });
        } catch (error) {
            errorCallback(error);
            toast(`${error.message}`);
        }
    },
    importDeckList: async (url, statusCallback, doneCallback, errorCallback, isRefresh = false) => {
        const analytics = getAnalytics();

        let oldDeckId = 'sdlfksdf';
        if (window.location.pathname.includes(`/details/deck/`)) {
            oldDeckId = window.location.pathname.substring(window.location.pathname.indexOf(`/deck/`) + 6);
        }

        try {
            statusCallback({
                type: `card`,
                card: 'Loading...',
                percentage: 1,
            });

            let requestUri = `${getDomainPrefix()}/decks?url=${url}`;
            if (oldDeckId && isRefresh) {
                requestUri = `${requestUri}&oldDeckId=${oldDeckId}`;
            }

            const request = await authFetch(requestUri, {
                method: 'POST',
                headers: { 'Content-type': 'application/json;charset=UTF-8' },
            });

            const response = await request.json();

            if (request.status !== 200) {
                throw response;
            }

            doneCallback({
                ...response,
                key: response.id,
            });
        } catch (error) {
            errorCallback(error);
            toast(`${error.message}`);
        }
    },
    postContactPing: async (payload, callback) => {
        const analytics = getAnalytics();

        try {
            const email = encodeURIComponent(payload.email);
            const name = encodeURIComponent(payload.name);
            const message = encodeURIComponent(payload.message);

            const request = await fetch(`${getDomainPrefix()}/contact?email=${email}&name=${name}&message=${message}`, {
                method: 'GET',
                headers: {
                    'Content-type': 'application/json;charset=UTF-8',
                },
            });

            const response = await request.json();

            if (request.status !== 200) {
                throw response;
            }
        } catch (error) {
            callback(); // TODO: add status to ui
            logEvent(analytics, 'email_failed', { payload });
        }

        logEvent(analytics, 'email_success', { payload });
        callback();
    },
};
